import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { ChevronRight, Eye, ArrowLeft, FileText } from 'lucide-react';
import type { SpoolCard, WeldJoint, WeldStepPanelItem } from './SpoolsProduction';
import type { DashboardProject } from '../../../types/project';
import { NDTInspectionModal } from './NDTInspectionModal';
import { CoatingQCModal } from './CoatingQCModal';
import { RaiseWorkflowNCRModal } from './RaiseWorkflowNCRModal';
import { useTeamMembers } from '../../../../../redux/ProductionData/hooks/useTeamMembers';
import type { MemberOption, SignaturePanelProps } from './components/weld/signatureTypes';
import {
  AcceptPanel,
  CoatingPanel,
  FitUpPanel,
  NDTPanel,
  PreheatPanel,
  VTPanel,
  WeldingPanel,
} from './components/panels';
import { WeldTable } from './components/weld/WeldTable';
import { WeldTelemetryModal } from './components/weld/WeldTelemetryModal';
import { weldJointStatusCell } from './components/weld/weldStatusBadge';
import { spoolTw } from './spoolUiPalette';
import type { SpoolStepPanelContext } from './components/weld/spoolStepPayloadHelpers';
import {
  getLastDedicatedRepairWeldingInfo,
  getLastPrimaryWeldingPanelItem,
  getRepairWeldingPanelPayload,
  getWeldStepPanelPayload,
  inferIntegratedRepairSourceFromWeld,
  weldPanelsRevision,
} from './components/weld/weldStepPanelHydration';
import * as spoolsbaseActions from '../../../../../redux/Spoolsbase/actions';
import { FATSpoolModal } from './FATSpoolModal';
import type { NcrRow, SpoolTrackerWeldStepType } from './spoolNcrApi';
import {
  workflowRepairOverridesStep,
  workflowStepShowsRepairCue,
  type SpoolModalWorkflowStep,
} from './repairWorkflowHelpers';
import { GetAllMaterials } from '../../../../../redux/Project/materialSaga';
import type { SpecificationDocument } from '../../../types';


import { buildBaseWorkflowStrip, type WorkflowPanelRoute } from './integratedRepairStrip';
import {
  buildWorkflowStripWithRepairCycles,
  firstRepairCycleStripKey,
  parseLegacyRepairStripKey,
  parseRepairCycleStripKey,
} from './stepRepairWorkflow';

// ─── types ────────────────────────────────────────────────────────────────────
type WeldStep =
  | 'fitup'
  | 'preheat'
  | 'welding'
  | 'repair_weld'
  | 'vt'
  | 'ndt'
  | 'coating'
  | 'accept';
/** Same mapping as AssemblyProduction — pipe `documentsData` → specification dropdown list. */
function mapPipeDocumentsToSpecifications(pipe: unknown): SpecificationDocument[] {
  if (!pipe || typeof pipe !== 'object') return [];
  const p = pipe as Record<string, unknown>;
  const dd = p.documentsData as { documents?: unknown[] } | undefined;
  const docs = dd?.documents;
  if (!Array.isArray(docs)) return [];
  return docs.map((doc: unknown, idx: number) => {
    const d = doc as Record<string, unknown>;
    return {
    id: d.docRefId != null ? String(d.docRefId) : `spec-${idx}`,
    fileName: String(d.fileName ?? ''),
    type: d.type as SpecificationDocument['type'],
    pipeId: p.pipeId != null ? String(p.pipeId) : '',
    projectId: p.projectId as number | undefined,
    projectTypeId: p.projectTypeId as number | undefined,
    pipeOrder: Number(p.pipeOrder ?? 0),
    fileUrl: d.fileUrl as string | undefined,
    s3Key: d.s3Key as string | undefined,
    uploadedBy: d.uploadedBy as string | undefined,
    uploadDate: String(d.uploadDate ?? ''),
    version: Number(d.version ?? 1),
    details: (d.details as Record<string, unknown>) || {},
    files: (d.files as SpecificationDocument['files']) || [],
    docRefId: Number(d.docRefId ?? 0),
  };
  });
}
/**
 * Determines which workflow step tab to open for a given weld joint.
 *
 * Progression order: fitup → preheat → welding → vt → ndt → coating → accept.
 * Spool-level FAT is opened from the register header or the workflow “FAT” action (separate full-screen modal).
 *
 * Uses `weldStepPanels` (from the API) to check which steps have been saved
 * so the tab always advances to the next incomplete step rather than jumping
 * straight to welding after fit-up.
 */
function getWeldPanelMode(panel: WeldStepPanelItem | null | undefined): string | null {
  if (!panel) return null;
  const pl = panel.payload;
  if (pl && typeof pl === 'object' && 'mode' in pl) {
    const m = String((pl as { mode?: unknown }).mode).trim();
    if (m) return m;
  }
  if (panel.submissionMode != null && String(panel.submissionMode).trim() !== '') {
    return String(panel.submissionMode).trim();
  }
  return null;
}

/** API may expose pass on the joint row, on the fit-up step `payload`, or only as a submitted `fitUp` step row. */
function isFitUpCompleteForWeld(weld: WeldJoint): boolean {
  const fit = weld.fitUp;
  if (fit && typeof fit === 'object' && fit != null && 'status' in fit) {
    if (String((fit as { status?: unknown }).status).toLowerCase() === 'pass') return true;
  }
  const fitRows = weld.weldStepPanels?.fitUp ?? [];
  for (const p of fitRows) {
    if (!p) continue;
    const pl = p.payload;
    if (pl && typeof pl === 'object' && pl != null) {
      const st = (pl as { status?: unknown }).status;
      if (st != null && String(st).toLowerCase() === 'pass') return true;
      const pm = (pl as { mode?: unknown }).mode;
      if (pm != null && String(pm).toLowerCase() === 'accept') return true;
    }
    const sm = String(p.submissionMode ?? '')
      .trim()
      .toLowerCase();
    if (['submitted', 'submit', 'final', 'complete', 'pass', 'accept'].some((k) => sm === k)) return true;
    const mode = getWeldPanelMode(p);
    if (mode) {
      const m = mode.toLowerCase();
      if (m === 'accept' || m.includes('submit') || m === 'pass' || m === 'final' || m === 'complete') return true;
    }
  }
  return false;
}

function getWorkflowStepForWeld(weld: WeldJoint | null): WeldStep {
  if (!weld) return 'fitup';

  const repairTab = workflowRepairOverridesStep(weld);
  if (repairTab) return repairTab as WeldStep;

  if (!isFitUpCompleteForWeld(weld)) return 'fitup';

  const panels = weld.weldStepPanels;

  // Preheat: if no preheat panel data saved yet, go to preheat
  if (!(panels?.preheat?.length ?? 0)) return 'preheat';

  const weldingRows = panels?.welding ?? [];
  if (weldingRows.length === 0) return 'welding';

  /** Ignore appended `repair_weld` rows when deciding if primary welding is submitted. */
  const lastPrimaryWelding = getLastPrimaryWeldingPanelItem(weld);
  const weldingMode = getWeldPanelMode(lastPrimaryWelding ?? weldingRows[0]);
  // Do not use `weld.status === 'pending'` alone: after "Submit weld record" the refetch
  // can still show `pending` briefly while the latest `weldStepPanels.welding[]` row has `mode: 'submit'`.
  // In that case we must not route back to the Weld tab (same bug as "snap" after save).
  if (weld.status === 'pending' && weldingMode !== 'submit') return 'welding';

  const statusForStep: WeldJoint['status'] =
    weld.status === 'pending' && weldingMode === 'submit' ? 'welded' : weld.status;

  // Past committed welding — use effective joint status
  switch (statusForStep) {
    case 'welded':
      // After welding: VT next (unless already saved or summarised)
      return weld.vtSummaryLine || (panels?.vt?.length ?? 0) > 0 ? 'ndt' : 'vt';
    case 'ndt_pass':
      // After NDT pass: coating next (unless already saved)
      return (panels?.coating?.length ?? 0) > 0 ? 'accept' : 'coating';
    case 'accepted':
      return 'accept';
    default:
      return 'welding';
  }
}

type SpoolSaveLast = {
  step: string;
  at: number;
  response: unknown;
  request?: Record<string, unknown> | null;
};

/** Payload `mode` for advance checks (lowercase; supports nested `payload.mode` if API mirrors body). */
function getSaveRequestMode(req: Record<string, unknown> | null | undefined): string | null {
  if (!req || typeof req !== 'object') return null;
  const top = (req as { mode?: unknown }).mode;
  if (top != null && String(top).trim() !== '') return String(top).trim().toLowerCase();
  const pl = (req as { payload?: { mode?: unknown } }).payload;
  if (pl && typeof pl === 'object' && pl != null && 'mode' in pl) {
    const m = (pl as { mode?: unknown }).mode;
    if (m != null && String(m).trim() !== '') return String(m).trim().toLowerCase();
  }
  return null;
}

/**
 * Auto-advances the workflow strip only for "commit" saves (e.g. Accept, Submit), not
 * for Save Draft — so draft + refetch does not jump the user to the next tab.
 */
function shouldAdvanceWorkflowAfterSpoolSave(last: SpoolSaveLast | null): boolean {
  if (!last) return false;
  const req = last.request;
  const mode = getSaveRequestMode(req ?? null);

  if (req == null) return false;

  if (mode === 'draft') return false;
  if (mode == null) {
    // Commit saves must include a known mode; avoid advancing on empty bodies
    return false;
  }

  switch (last.step) {
    case 'fitUp':
      return mode === 'accept';
    case 'preheat':
      return mode === 'confirm_weld';
    case 'welding':
      return mode === 'submit';
    case 'vt':
      return mode === 'pass_vt';
    case 'ndt':
      return mode === 'submit';
    case 'coating':
      return mode === 'pass_release';
    case 'accept':
      return mode === 'accept_complete';
    case 'ncr':
      return false;
    default:
      return false;
  }
}

/** e.g. `Size 10" X 0.5` — adds `"` to bare numeric OD only; wall thickness shown as stored. */
function selectedPipeSpecString(pipe: unknown): string | null {
  if (!pipe || typeof pipe !== 'object') return null;
  const p = pipe as Record<string, unknown>;
  const rawSize = p.pipeSize ?? p.size;
  const rawWt = p.wallThickness ?? p.wall_thickness ?? p.wt;
  const toStr = (v: unknown) => (v == null ? '' : String(v).trim());
  const size = toStr(rawSize);
  const wt = toStr(rawWt);
  if (!size && !wt) return null;

  /** Nominal OD: bare numbers get a inch mark (e.g. `10` → `10"`). */
  const inchMark = (s: string) => {
    if (!s) return s;
    if (/["″']/.test(s)) return s;
    if (/^\d+(\.\d+)?$/.test(s)) return `${s}"`;
    return s;
  };

  const left = inchMark(size);
  const right = wt;
  if (left && right) return `Size ${left} X ${right}`;
  if (left) return `Size ${left}`;
  if (right) return `Wall ${right}`;
  return null;
}

/** When production has not passed a pipe row, show joint side text from mock welds. */
function jointPipeFallback(spool: SpoolCard, selectedWeld: WeldJoint | null): string | null {
  const primary = selectedWeld ?? spool.weldJoints[0] ?? null;
  const line = primary?.sideADesc?.trim() || primary?.sideBDesc?.trim();
  if (line) return line;
  const anyJ = spool.weldJoints.find((w) => w.sideADesc?.trim() || w.sideBDesc?.trim());
  return anyJ?.sideADesc?.trim() || anyJ?.sideBDesc?.trim() || null;
}

// ─── main modal ───────────────────────────────────────────────────────────────
interface SpoolDetailModalProps {
  spool: SpoolCard | null;
  open: boolean;
  onClose: () => void;
  project?: DashboardProject;
  selectedpipe?: any;
}

export const SpoolDetailModal: React.FC<SpoolDetailModalProps> = ({ spool, open, onClose, project, selectedpipe }) => {
  const dispatch = useDispatch();
  /** Matches `stripKey` from `buildIntegratedRepairWorkflowStrip` (unique per pill, including VT/NDT retest). */
  const [activeStripKey, setActiveStripKey] = useState<string>('fitup');
  const [repairFlowActive, setRepairFlowActive] = useState(false);
  const [repairNcrContext, setRepairNcrContext] = useState<{
    row: NcrRow;
    repairNo: number;
    repairSourceStep: SpoolTrackerWeldStepType;
  } | null>(null);
  /** Full repair cycle numbers appended after production Accept (1, 2, …). */
  const [repairCycleNumbers, setRepairCycleNumbers] = useState<number[]>([]);
  const repairCounterRef = useRef(0);
  const workflowStepsRef = useRef(buildBaseWorkflowStrip());
  const [portalView, setPortalView] = useState<'register' | 'workflow'>('register');
  const [isFATSpoolModalOpen, setIsFATSpoolModalOpen] = useState(false);
  const [selectedWeldId, setSelectedWeldId] = useState<string | null>(null);
  /** Avoid resetting the user's step tab on unrelated `spool` re-renders; only sync on weld/spool/context change. */
  const workflowStepSyncRef = useRef<{ selectedWeldId: string | null; spoolId: number } | null>(null);

  // ── step auto-advance after save ──────────────────────────────────────────
  /** `lastSaved` changes on every successful weld-step save. */
  const lastSaved = useSelector((state: any) => state.spoolsbase?.lastSaved ?? null);
  /** Used to distinguish "old save from a previous session" vs "new save during this session". */
  const isLastSavedInitRef = useRef(false);
  const prevLastSavedRef = useRef<any>(null);
  /** Mirror of `activeStripKey` for the save-advance effect. */
  const activeStripKeyRef = useRef<string>(activeStripKey);
  useEffect(() => {
    activeStripKeyRef.current = activeStripKey;
  }, [activeStripKey]);

  // ── team members ──────────────────────────────────────────────────────────
  const { teamMembers, loadTeamMembers, teamMembersLoading } = useTeamMembers();
  const [qcList, setQcList] = useState<MemberOption[]>([]);
  const [welderList, setWelderList] = useState<MemberOption[]>([]);
  const [customerRepresentativeList, setCustomerRepresentativeList] = useState<MemberOption[]>([]);
  const [coatingList, setCoatingList] = useState<MemberOption[]>([]);

  // ── per-role signature state ──────────────────────────────────────────────
  const [selectedQcId, setSelectedQcId] = useState<string | null>(null);
  const [selectedWelderId, setSelectedWelderId] = useState<string | null>(null);
  const [selectedCoatingId, setSelectedCoatingId] = useState<string | null>(null);
  const [qcSigData, setQcSigData] = useState<any>(null);
  const [welderSigData, setWelderSigData] = useState<any>(null);
  const [coatingSigData, setCoatingSigData] = useState<any>(null);
  const [isNDTInspectionModalOpen, setIsNDTInspectionModalOpen] = useState(false);
  const [isCoatingQCModalOpen, setIsCoatingQCModalOpen] = useState(false);
  const [isRaiseNcrOpen, setIsRaiseNcrOpen] = useState(false);
  /** Row from a panel NCR table — opens Raise NCR with the same form prefilled. */
  const [ncrListPrefillRow, setNcrListPrefillRow] = useState<NcrRow | null>(null);
  const [isWeldTelemetryModalOpen, setIsWeldTelemetryModalOpen] = useState(false);
  /** Preloaded when modal opens so Welding uses WPS / drawing / gas / wire lists (aligned with Production sheet). */
  const [weldingMaterialsBundle, setWeldingMaterialsBundle] = useState<{
    gas?: unknown[];
    wireLot?: unknown[];
    pipes?: unknown[];
  }>({});
  const [weldingSpecifications, setWeldingSpecifications] = useState<SpecificationDocument[]>([]);

  useEffect(() => {
    if (!open || !selectedpipe || typeof selectedpipe !== 'object') {
      setWeldingSpecifications([]);
      setWeldingMaterialsBundle({});
      return;
    }
    setWeldingSpecifications(mapPipeDocumentsToSpecifications(selectedpipe));
    const projectId = (selectedpipe as { projectId?: unknown }).projectId;
    const projectTypeId = (selectedpipe as { projectTypeId?: unknown }).projectTypeId;
    const pipeId = (selectedpipe as { pipeId?: unknown }).pipeId;
    if (projectId == null || projectTypeId == null || pipeId == null) return;
    let cancelled = false;
    void (async () => {
      const data = await GetAllMaterials({
        project_id: projectId,
        project_type_id: projectTypeId,
        pipe_id: pipeId,
      });
      if (cancelled || data == null || typeof data !== 'object') return;
      const d = data as Record<string, unknown>;
      setWeldingMaterialsBundle({
        gas: Array.isArray(d.gas) ? d.gas : [],
        wireLot: Array.isArray(d.wireLot) ? d.wireLot : [],
        pipes: Array.isArray(d.pipes) ? d.pipes : [],
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [open, selectedpipe]);

  const makeSigChangeHandler = (setter: React.Dispatch<React.SetStateAction<any>>) =>
    (data: any) => setter((prev: any) => ({
      ...prev,
      signature: {
        ...(prev?.signature ?? {}),
        [data.type]: data.signature
          ? {
              signature: data.signature,
              name: data.name,
              comment: data.comment,
              stampImage: data.stampImage,
              timestamp: new Date().toISOString(),
            }
          : undefined,
      },
    }));

  // Load team members when modal opens
  useEffect(() => {
    if (open && project?.project?.projectId) {
      loadTeamMembers(project.project.projectId);
    }
  }, [open, project?.project?.projectId]);

  // Process team members into role-specific lists
  useEffect(() => {
    console.log('teamMembers', teamMembers);
    if (!teamMembersLoading && teamMembers && teamMembers.length > 0) {
      const toOption = (m: any): MemberOption => ({
        value: m.team_member_id.toString(),
        name: `${m.user.first_name} ${m.user.last_name}`,
        label: `${m.user.first_name} ${m.user.last_name} (${m.user.email})`,
        userId: m?.user_id?.toString(),
      });
      setQcList(teamMembers.filter((m: any) =>
        ['Quality Control', 'QC', 'QA/QC'].includes(m?.team_member_role)).map(toOption));
      setWelderList(teamMembers.filter((m: any) =>
        ['Welder', 'Welding Inspector'].includes(m?.team_member_role)).map(toOption));
      setCustomerRepresentativeList(
        teamMembers
          .filter((m: any) =>
            ['Customer Representative', 'Client Representative'].includes(m?.team_member_role)
          )
          .map(toOption)
      );
      setCoatingList(teamMembers.filter((m: any) =>
        ['Coating Inspector', 'Coating' ,'Material Manager – Coating'].includes(m?.team_member_role)).map(toOption));
    }
  }, [teamMembers, teamMembersLoading]);

  useEffect(() => {
    if (!open) {
      setPortalView('register');
      setSelectedWeldId(null);
      setRepairFlowActive(false);
      setRepairNcrContext(null);
      setRepairCycleNumbers([]);
      repairCounterRef.current = 0;
      setIsNDTInspectionModalOpen(false);
      setIsCoatingQCModalOpen(false);
      setIsRaiseNcrOpen(false);
      setIsWeldTelemetryModalOpen(false);
      setIsFATSpoolModalOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (portalView === 'register') {
      setRepairFlowActive(false);
      setRepairNcrContext(null);
    }
  }, [portalView]);

  useEffect(() => {
    setRepairCycleNumbers([]);
    repairCounterRef.current = 0;
  }, [selectedWeldId, spool?.id]);

  /**
   * Advance the active workflow step whenever a weld-step save succeeds.
   *
   * Logic:
   *  - On modal open (or re-open): snapshot the current `lastSaved` so we
   *    don't react to saves from a previous session.
   *  - On modal close: reset the init flag so the snapshot runs fresh next time.
   *  - When `lastSaved` changes while the modal is open: for non-draft saves, advance
   *    one step. We do **not** clear `workflowStepSyncRef` here — if we did, the spool
   *    refetch after save would re-run the weld sync effect, and `getWorkflowStepForWeld`
   *    can briefly return the previous step (e.g. VT) before `weldStepPanels.vt` arrives,
   *    which would reset the tab the user just advanced (e.g. NDT).
   */
  useEffect(() => {
    if (!open) {
      isLastSavedInitRef.current = false;
      prevLastSavedRef.current = null;
      return;
    }
    // First run after modal opens — snapshot current value; do not advance.
    if (!isLastSavedInitRef.current) {
      isLastSavedInitRef.current = true;
      prevLastSavedRef.current = lastSaved;
      return;
    }
    // No new save since last render.
    if (!lastSaved || lastSaved === prevLastSavedRef.current) return;

    // New save detected — update snapshot.
    prevLastSavedRef.current = lastSaved;

    if (!shouldAdvanceWorkflowAfterSpoolSave(lastSaved as SpoolSaveLast)) return;

    const currentSteps = workflowStepsRef.current;
    const curIdx = currentSteps.findIndex((s) => s.stripKey === activeStripKeyRef.current);
    const nextIdx = curIdx + 1;
    if (nextIdx < currentSteps.length) {
      setActiveStripKey(currentSteps[nextIdx].stripKey);
    }
  }, [lastSaved, open]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Weld telemetry is only meaningful after the joint is welded (hide while status is still pending). */
  useEffect(() => {
    if (!open || !spool) return;
    const ctx =
      (selectedWeldId ? spool.weldJoints.find((w) => w.id === selectedWeldId) ?? null : null) ??
      spool.weldJoints[0] ??
      null;
    if (!ctx || ctx.status === 'pending') setIsWeldTelemetryModalOpen(false);
  }, [open, spool, selectedWeldId]);

  /** When entering weld steps (from register) or changing selected weld / spool, jump to the step that matches that weld. */
  useEffect(() => {
    if (!open) {
      workflowStepSyncRef.current = null;
      return;
    }
    if (portalView === 'register') {
      workflowStepSyncRef.current = null;
      return;
    }
    if (repairFlowActive) return;
    if (!spool) return;
    const weld: WeldJoint | null =
      (selectedWeldId
        ? spool.weldJoints.find((w) => w.id === selectedWeldId) ?? null
        : null) ??
      spool.weldJoints[0] ??
      null;
    const prev = workflowStepSyncRef.current;
    const needSync =
      !prev ||
      prev.selectedWeldId !== selectedWeldId ||
      String(prev.spoolId) !== String(spool.id);
    if (needSync) {
      const nextStep = getWorkflowStepForWeld(weld);
      setRepairFlowActive(nextStep === 'repair_weld');
      setActiveStripKey(nextStep);
      workflowStepSyncRef.current = { selectedWeldId, spoolId: spool.id };
    }
  }, [open, portalView, selectedWeldId, spool, repairFlowActive]);

  /** Bumps when the active weld’s `weldStepPanels` change (e.g. after any step save + refetch). */
  const weldStepPanelsSyncKey = useMemo(() => {
    if (!spool) return '';
    const w =
      (selectedWeldId ? spool.weldJoints.find((x) => x.id === selectedWeldId) ?? null : null) ??
      spool.weldJoints[0] ??
      null;
    return w ? `${w.id}:${weldPanelsRevision(w)}` : '';
  }, [spool, selectedWeldId]);

  const weldForSteps = useMemo(() => {
    if (!spool) return null;
    const sel = selectedWeldId ? spool.weldJoints.find((w) => w.id === selectedWeldId) ?? null : null;
    return sel ?? spool.weldJoints[0] ?? null;
  }, [spool, selectedWeldId]);

  const weldPanelsRev = useMemo(() => weldPanelsRevision(weldForSteps), [weldForSteps]);

  const apiRepairSourceStep = useMemo(
    () => inferIntegratedRepairSourceFromWeld(weldForSteps),
    [weldForSteps, weldPanelsRev]
  );

  const inferredRepairNoFromApi = useMemo(
    () => getLastDedicatedRepairWeldingInfo(weldForSteps)?.repairNo ?? null,
    [weldForSteps, weldPanelsRev]
  );
  const workflowSteps = useMemo(
    () => buildWorkflowStripWithRepairCycles(repairCycleNumbers),
    [repairCycleNumbers]
  );
  workflowStepsRef.current = workflowSteps;

  const activeStripRepairCycle =
    workflowSteps.find((s) => s.stripKey === activeStripKey)?.repairCycle ??
    parseRepairCycleStripKey(activeStripKey);

  const repairNoForPanel =
    activeStripRepairCycle?.repairNo ??
    parseLegacyRepairStripKey(activeStripKey)?.repairNo ??
    repairNcrContext?.repairNo ??
    inferredRepairNoFromApi;

  const repairSourceForPanel = repairNcrContext?.repairSourceStep ?? apiRepairSourceStep;

  const isRepairWeldingStrip =
    /^repair_welding_\d+$/.test(activeStripKey) ||
    activeStripKey === 'repair_weld';

  /**
   * Fit-up saves a flat `signature` (from `buildQcSignaturePayload`) on `weldStepPanels.fitUp.payload`.
   * Hydrate shared QC dropdown + pad so refetch restores the inspector signature.
   */
  useEffect(() => {
    if (!open || !spool || (activeStripKey !== 'fitup' && !/^repair_fitup_\d+$/.test(activeStripKey))) return;
    const weld =
      (selectedWeldId ? spool.weldJoints.find((w) => w.id === selectedWeldId) ?? null : null) ??
      spool.weldJoints[0] ??
      null;
    if (!weld) return;

    const pl = getWeldStepPanelPayload(weld, 'fitUp');
    if (!pl || Object.keys(pl).length === 0) {
      setSelectedQcId(null);
      setQcSigData(null);
      return;
    }

    const qRaw = (pl.signature ?? pl.qcSignature) as unknown;
    const qSig = qRaw && typeof qRaw === 'object' ? (qRaw as Record<string, unknown>) : null;
    if (!qSig) {
      setSelectedQcId(null);
      setQcSigData(null);
      return;
    }

    const qtm = qSig.qcTeamMemberId ?? qSig.qc_team_member_id;
    if (qtm != null && String(qtm) !== '') setSelectedQcId(String(qtm));
    else setSelectedQcId(null);

    const img = String(qSig.signatureImage ?? qSig.signature_image ?? '');
    if (img.length > 0 || qSig.signed === true) {
      setQcSigData({
        signature: {
          qc: {
            name: String(qSig.signerNameFromPad ?? qSig.selectedName ?? ''),
            signature: img,
            comment: String(qSig.comment ?? ''),
            stampImage: String(qSig.stampImage ?? ''),
            timestamp: String(qSig.timestamp ?? new Date().toISOString()),
          },
        },
      });
    } else {
      setQcSigData(null);
    }
  }, [open, spool, selectedWeldId, activeStripKey, weldStepPanelsSyncKey]);

  /**
   * Welding signatures live on `weldStepPanels.welding[].payload`. Primary weld uses the **primary** row;
   * repair weld uses the row whose `weldingStepKind === 'repair_weld'` matches `repairNo`.
   */
  useEffect(() => {
    if (!open || !spool || (activeStripKey !== 'welding' && !isRepairWeldingStrip)) return;
    const weld =
      (selectedWeldId ? spool.weldJoints.find((w) => w.id === selectedWeldId) ?? null : null) ??
      spool.weldJoints[0] ??
      null;
    if (!weld) return;

    const pl =
      isRepairWeldingStrip && repairNoForPanel != null
        ? getRepairWeldingPanelPayload(weld, repairNoForPanel)
        : getWeldStepPanelPayload(weld, 'welding');
    if (!pl || Object.keys(pl).length === 0) {
      setSelectedWelderId(null);
      setSelectedQcId(null);
      setWelderSigData(null);
      return;
    }

    const wRaw = pl.customerRepSignature ?? pl.customer_rep_signature ?? pl.welderSignature;
    const wSig = wRaw && typeof wRaw === 'object' ? (wRaw as Record<string, unknown>) : null;
    const qRaw = pl.qcSignature;
    const qSig = qRaw && typeof qRaw === 'object' ? (qRaw as Record<string, unknown>) : null;

    if (wSig) {
      const wtm =
        wSig.customerRepTeamMemberId ??
        wSig.customer_rep_team_member_id ??
        wSig.welderTeamMemberId;
      if (wtm != null && wtm !== '') setSelectedWelderId(String(wtm));
    } else {
      setSelectedWelderId(null);
    }

    if (qSig) {
      const qtm = qSig.qcTeamMemberId;
      if (qtm != null && qtm !== '') setSelectedQcId(String(qtm));
    } else {
      setSelectedQcId(null);
    }

    const customer =
      wSig && (String(wSig.signatureImage ?? '').length > 0 || wSig.signed === true)
        ? {
            name: String(wSig.signerNameFromPad ?? wSig.selectedName ?? ''),
            signature: String(wSig.signatureImage ?? ''),
            comment: String(wSig.comment ?? ''),
            stampImage: String(wSig.stampImage ?? ''),
            timestamp: String(wSig.timestamp ?? new Date().toISOString()),
          }
        : null;

    const qc =
      qSig && (String(qSig.signatureImage ?? '').length > 0 || qSig.signed === true)
        ? {
            name: String(qSig.signerNameFromPad ?? qSig.selectedName ?? ''),
            signature: String(qSig.signatureImage ?? ''),
            comment: String(qSig.comment ?? ''),
            stampImage: String(qSig.stampImage ?? ''),
            timestamp: String(qSig.timestamp ?? new Date().toISOString()),
          }
        : null;

    if (!customer && !qc) {
      setWelderSigData(null);
    } else {
      setWelderSigData({
        signature: {
          ...(customer ? { customer } : {}),
          ...(qc ? { qc } : {}),
        },
      });
    }
  }, [
    open,
    spool,
    selectedWeldId,
    activeStripKey,
    weldStepPanelsSyncKey,
    repairNoForPanel,
    isRepairWeldingStrip,
  ]);

  /**
   * Coating step payload includes `coatingInspector` — `CoatingPanel` only passes `coatingSignatureData` into `Signature` (customer slot).
   */
  useEffect(() => {
    if (!open || !spool || (activeStripKey !== 'coating' && !/^repair_coating_\d+$/.test(activeStripKey))) return;
    const weld =
      (selectedWeldId ? spool.weldJoints.find((w) => w.id === selectedWeldId) ?? null : null) ??
      spool.weldJoints[0] ??
      null;
    if (!weld) return;

    const pl = getWeldStepPanelPayload(weld, 'coating');
    if (!pl || Object.keys(pl).length === 0) {
      setSelectedCoatingId(null);
      setCoatingSigData(null);
      return;
    }

    const cRaw = pl.coatingInspector;
    const cSig = cRaw && typeof cRaw === 'object' ? (cRaw as Record<string, unknown>) : null;
    if (!cSig) {
      setSelectedCoatingId(null);
      setCoatingSigData(null);
      return;
    }

    const ctm = cSig.coatingTeamMemberId;
    if (ctm != null && ctm !== '') setSelectedCoatingId(String(ctm));
    else setSelectedCoatingId(null);

    const img = cSig.signatureImage != null ? String(cSig.signatureImage) : '';
    if (img.length > 0 || cSig.signed === true) {
      setCoatingSigData({
        signature: {
          customer: {
            name: String(cSig.signerNameFromPad ?? cSig.selectedName ?? ''),
            signature: img,
            comment: String(cSig.comment ?? ''),
            stampImage: String(cSig.stampImage ?? ''),
            timestamp: String(cSig.timestamp ?? new Date().toISOString()),
          },
        },
      });
    } else {
      setCoatingSigData(null);
    }
  }, [open, spool, selectedWeldId, activeStripKey, weldStepPanelsSyncKey]);

  /**
   * Accept step stores QC `signature` (same shape as Fit-up) on `weldStepPanels.accept.payload`.
   * Hydrate when the tab is open or after refetch / joint switch so edit matches the server row.
   */
  useEffect(() => {
    if (!open || !spool || (activeStripKey !== 'accept' && !/^repair_accept_\d+$/.test(activeStripKey))) return;
    const weld =
      (selectedWeldId ? spool.weldJoints.find((w) => w.id === selectedWeldId) ?? null : null) ??
      spool.weldJoints[0] ??
      null;
    if (!weld) return;

    const pl = getWeldStepPanelPayload(weld, 'accept');
    if (!pl || Object.keys(pl).length === 0) {
      setSelectedQcId(null);
      setQcSigData(null);
      return;
    }

    const qRaw = (pl.signature ?? pl.qcSignature) as unknown;
    const qSig = qRaw && typeof qRaw === 'object' ? (qRaw as Record<string, unknown>) : null;
    if (!qSig) {
      setSelectedQcId(null);
      setQcSigData(null);
      return;
    }

    const qtm = qSig.qcTeamMemberId ?? qSig.qc_team_member_id;
    if (qtm != null && String(qtm) !== '') setSelectedQcId(String(qtm));
    else setSelectedQcId(null);

    const img = String(qSig.signatureImage ?? qSig.signature_image ?? '');
    if (img.length > 0 || qSig.signed === true) {
      setQcSigData({
        signature: {
          qc: {
            name: String(qSig.signerNameFromPad ?? qSig.selectedName ?? ''),
            signature: img,
            comment: String(qSig.comment ?? ''),
            stampImage: String(qSig.stampImage ?? ''),
            timestamp: String(qSig.timestamp ?? new Date().toISOString()),
          },
        },
      });
    } else {
      setQcSigData(null);
    }
  }, [open, spool, selectedWeldId, activeStripKey, weldStepPanelsSyncKey]);

  const sigProps: SignaturePanelProps = {
    qcList, selectedQcId, onQcChange: setSelectedQcId,
    qcSignatureData: qcSigData, onQcSignatureChange: makeSigChangeHandler(setQcSigData),
    welderList, selectedWelderId, onWelderChange: setSelectedWelderId,
    welderSignatureData: welderSigData, onWelderSignatureChange: makeSigChangeHandler(setWelderSigData),
    coatingList, selectedCoatingId, onCoatingChange: setSelectedCoatingId,
    coatingSignatureData: coatingSigData, onCoatingSignatureChange: makeSigChangeHandler(setCoatingSigData),
  };

  const handleSubmittedNcrListRow = useCallback((row: NcrRow) => {
    setNcrListPrefillRow(row);
    setIsRaiseNcrOpen(true);
  }, []);

  const handleOpenRaiseNcr = useCallback(() => {
    setNcrListPrefillRow(null);
    setIsRaiseNcrOpen(true);
  }, []);

  const handleAddRepairCycle = useCallback(() => {
    repairCounterRef.current += 1;
    const repairNo = repairCounterRef.current;
    setRepairCycleNumbers((prev) => [...prev, repairNo]);
    setRepairFlowActive(true);
    setPortalView('workflow');
    setActiveStripKey(firstRepairCycleStripKey(repairNo));
  }, []);

  const activeStripRoute = workflowSteps.find((s) => s.stripKey === activeStripKey);
  const ncrWeldStepFromStrip = (activeStripRoute?.panel ?? 'fitup') as SpoolTrackerWeldStepType;
  const currentStepIdx = workflowSteps.findIndex((s) => s.stripKey === activeStripKey);

  if (!spool) return null;

  const selectedWeld = selectedWeldId
    ? spool.weldJoints.find((w) => w.id === selectedWeldId) ?? null
    : null;
  const showWeldTelemetryButton = weldForSteps != null && weldForSteps.status !== 'pending';

  const stepPanelContext: SpoolStepPanelContext = {
    projectId: project?.project?.projectId ?? null,
    pipeId: selectedpipe?.pipeId ?? selectedpipe?.pipe_id ?? selectedpipe?.id ?? null,
    projectTypeId: (() => {
      const v = (selectedpipe as { projectTypeId?: number | string | null } | null | undefined)
        ?.projectTypeId;
      return v == null || v === '' ? null : v;
    })(),
    spoolId: spool.id,
    spoolName: spool.name,
    weldId: weldForSteps?.id ?? null,
    weldNumber: weldForSteps?.number ?? null,
  };

  const pipeSpecLabel = selectedPipeSpecString(selectedpipe);
  const jointPipeLine = jointPipeFallback(spool, selectedWeld);
  const pipeHeaderLine = pipeSpecLabel ?? jointPipeLine ?? '—';

  const renderPanel = () => {
    const route = workflowSteps.find((s) => s.stripKey === activeStripKey);
    const panel: WorkflowPanelRoute = route?.panel ?? 'fitup';

    switch (panel) {
      case 'fitup':
        return (
          <FitUpPanel
            weld={weldForSteps}
            sig={sigProps}
            context={stepPanelContext}
            specifications={weldingSpecifications}
            repairNo={route?.repairCycle?.repairNo ?? undefined}
            onFitUpSave={(payload) => {
              void dispatch(spoolsbaseActions.saveSpoolFitUp(payload));
            }}
            onSubmittedNcrClick={handleSubmittedNcrListRow}
            onOpenRaiseNcr={handleOpenRaiseNcr}
          />
        );
      case 'preheat':
        return (
          <PreheatPanel
            weld={weldForSteps}
            context={stepPanelContext}
            specifications={weldingSpecifications}
            repairNo={route?.repairCycle?.repairNo ?? undefined}
            onPreheatSave={(p) => {
              void dispatch(spoolsbaseActions.saveSpoolPreheat(p));
            }}
            onSubmittedNcrClick={handleSubmittedNcrListRow}
            onOpenRaiseNcr={handleOpenRaiseNcr}
          />
        );
      case 'welding': {
        const repairCycleWeld = route?.repairCycle?.cycleStep === 'welding';
        return (
          <WeldingPanel
            spool={spool}
            weld={weldForSteps}
            sig={sigProps}
            context={stepPanelContext}
            mode={repairCycleWeld ? 'repair_weld' : 'welding'}
            repairFromNcr={repairCycleWeld ? null : repairNcrContext?.row ?? null}
            repairNo={repairCycleWeld ? route?.repairCycle?.repairNo : undefined}
            repairSourceStep={repairCycleWeld ? 'welding' : repairSourceForPanel}
            specifications={weldingSpecifications}
            materials={weldingMaterialsBundle}
            customerRepresentativeList={customerRepresentativeList}
            onWeldingSave={(p) => {
              void dispatch(spoolsbaseActions.saveSpoolWelding(p));
            }}
            onSubmittedNcrClick={handleSubmittedNcrListRow}
            onOpenRaiseNcr={handleOpenRaiseNcr}
          />
        );
      }
      case 'repair_weld':
        return (
          <WeldingPanel
            spool={spool}
            weld={weldForSteps}
            sig={sigProps}
            context={stepPanelContext}
            mode="repair_weld"
            repairFromNcr={repairNcrContext?.row ?? null}
            repairNo={repairNoForPanel}
            repairSourceStep={repairSourceForPanel}
            specifications={weldingSpecifications}
            materials={weldingMaterialsBundle}
            customerRepresentativeList={customerRepresentativeList}
            onWeldingSave={(p) => {
              void dispatch(spoolsbaseActions.saveSpoolWelding(p));
            }}
            onSubmittedNcrClick={handleSubmittedNcrListRow}
            onOpenRaiseNcr={handleOpenRaiseNcr}
          />
        );
      case 'vt':
        return (
          <VTPanel
            weld={weldForSteps}
            sig={sigProps}
            context={stepPanelContext}
            vtPhase={route?.vtPhase}
            repairNo={repairNoForPanel ?? undefined}
            onVtSave={(p) => {
              void dispatch(spoolsbaseActions.saveSpoolVt(p));
            }}
            onSubmittedNcrClick={handleSubmittedNcrListRow}
            onNavigateToStep={(step: SpoolModalWorkflowStep) => setActiveStripKey(String(step))}
            onOpenRaiseNcr={handleOpenRaiseNcr}
          />
        );
      case 'ndt':
        return (
          <NDTPanel
            weld={weldForSteps}
            context={stepPanelContext}
            ndtPhase={route?.ndtPhase}
            repairNo={repairNoForPanel ?? undefined}
            onNdtSave={(p) => {
              void dispatch(spoolsbaseActions.saveSpoolNdt(p));
            }}
            onSubmittedNcrClick={handleSubmittedNcrListRow}
            onNavigateToStep={(step: SpoolModalWorkflowStep) => setActiveStripKey(String(step))}
            onOpenRaiseNcr={handleOpenRaiseNcr}
          />
        );
      case 'coating':
        return (
          <CoatingPanel
            weld={weldForSteps}
            sig={sigProps}
            context={stepPanelContext}
            repairNo={route?.repairCycle?.repairNo ?? undefined}
            onCoatingSave={(p) => {
              void dispatch(spoolsbaseActions.saveSpoolCoating(p));
            }}
            onSubmittedNcrClick={handleSubmittedNcrListRow}
            onOpenRaiseNcr={handleOpenRaiseNcr}
          />
        );
      case 'accept':
        return (
          <AcceptPanel
            spool={spool}
            weld={weldForSteps}
            sig={sigProps}
            context={stepPanelContext}
            repairNo={route?.repairCycle?.repairNo ?? undefined}
            onAcceptSave={(p) => {
              void dispatch(spoolsbaseActions.saveSpoolAccept(p));
            }}
            onSubmittedNcrClick={handleSubmittedNcrListRow}
            onOpenRaiseNcr={handleOpenRaiseNcr}
            onAddRepairCycle={
              route?.repairCycle ? undefined : handleAddRepairCycle
            }
          />
        );
      default:
        return null;
    }
  };
  const currentStep = workflowSteps[currentStepIdx] ?? workflowSteps[0];

  const chipMuted =
    'inline-flex h-7 max-w-full shrink-0 items-center rounded px-2.5 text-xs font-medium leading-none text-gray-700 bg-gray-100';
  const chipPipe =
    'inline-flex h-7 max-w-full min-w-0 shrink items-center rounded border border-gray-200 bg-gray-50 px-2.5 text-xs font-semibold leading-none text-gray-900';

  const spoolHeaderMeta = (
    <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
      <span className={chipPipe} title={pipeHeaderLine}>
        <span className="truncate">{pipeHeaderLine}</span>
      </span>
      <span className={chipMuted}>{spool.type}</span>
      <span className={chipMuted}>{spool.length}</span>
      <span className={chipMuted}>
        {spool.welds} welds
      </span>
      {selectedWeld ? (
        <>
          <span className="hidden h-4 w-px shrink-0 self-center bg-gray-200 sm:block" aria-hidden />
          <span className={`${chipMuted} gap-1.5`}>
            <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-gray-500">Weld</span>
            <span className="truncate font-mono font-bold text-gray-900">{selectedWeld.number}</span>
          </span>
          <span className={`${chipMuted} max-w-[140px] min-w-0 gap-1`}>
            <span className="shrink-0 text-gray-500">Type</span>
            <span className="truncate">{selectedWeld.type}</span>
          </span>
          <span className={`${chipMuted} max-w-[200px] min-w-0 gap-1 sm:max-w-[240px]`}>
            <span className="shrink-0 text-gray-500">Welder</span>
            <span className="truncate">{selectedWeld.welder}</span>
          </span>
          <span className="inline-flex min-w-0 max-w-[min(100%,14rem)] shrink-0 items-start gap-1.5 text-xs">
            <span className="shrink-0 text-gray-500">Status</span>
            {weldJointStatusCell(selectedWeld)}
          </span>
        </>
      ) : null}
    </div>
  );



  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="w-screen h-screen max-w-none m-0 p-0 gap-0 border-0 bg-white shadow-none rounded-none [&>button]:hidden">
          <div className="flex flex-col relative z-20 h-screen overflow-hidden">

            {portalView === 'register' ? (
              <>
                <DialogHeader className="flex flex-shrink-0 flex-row items-center justify-between gap-3 border-b bg-white px-4 py-4 sm:px-6">
                  <div className="flex min-w-0 flex-1 items-center gap-4">
                    <Button
                      onClick={onClose}
                      variant="outline"
                      className="shrink-0 rounded-lg p-2 transition-colors hover:bg-gray-100"
                    >
                      <ArrowLeft className="h-5 w-5 text-gray-500" />
                    </Button>
                    <div className="min-w-0">
                      <DialogTitle className="text-lg font-bold text-gray-900">
                        Weld Management — {spool.name}
                      </DialogTitle>
                      {spoolHeaderMeta}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                    <Button
                      type="button"
                      className="flex items-center gap-2"
                      onClick={() => setIsFATSpoolModalOpen(true)}
                    >
                      <FileText className="h-4 w-4" />
                      FAT
                    </Button>
                    {/* <Button
                    //  type="button"
                     // variant="outline"
                     // size="sm"
                      className="flex items-center gap-2"
                      onClick={() => setIsNDTInspectionModalOpen(true)}
                    >
                      <FileText className="h-4 w-4" />
                      NDT
                    </Button>
                    <Button
                     // type="button"
                    //  variant="outline"
                      //size="sm"
                      className="flex items-center gap-2"
                      onClick={() => setIsCoatingQCModalOpen(true)}
                    >
                      <FileText className="h-4 w-4" />
                      Coating
                    </Button> */}
                    
                  </div>
                </DialogHeader>

              <div className="flex-1 overflow-y-auto">
                <div className="px-4 sm:px-6 py-6 max-w-full mx-auto space-y-6">
                 
                  <WeldTable
                    spool={spool}
                    sig={sigProps}
                    selectedWeldId={selectedWeldId}
                    onSelectedWeldChange={setSelectedWeldId}
                    onContinueToWorkflow={() => setPortalView('workflow')}
                  />
                  {/* {selectedWeld && (
                    <div className="flex flex-col sm:flex-row sm:justify-end gap-2 pt-2 border-t border-gray-100">
                      <Button type="button" variant="outline" size="sm" onClick={() => setSelectedWeldId(null)}>
                        Clear selection
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        className="bg-[#e04530] hover:bg-[#e04530]/90 text-white"
                        onClick={() => setPortalView('workflow')}
                      >
                        Continue to weld steps
                      </Button>
                    </div>
                  )} */}
                </div>
              </div>
            </>
            ) : (
            <>
              <DialogHeader className="flex flex-shrink-0 flex-row items-start justify-between gap-3 border-b bg-white px-4 py-4 sm:px-6">
                <div className="flex min-w-0 flex-1 items-start gap-4">
                  <Button
                    type="button"
                    onClick={() => setPortalView('register')}
                    variant="outline"
                    className="mt-0.5 shrink-0 rounded-lg p-2 transition-colors hover:bg-gray-100"
                    aria-label="Back to weld list"
                  >
                    <ArrowLeft className="h-5 w-5 text-gray-500" />
                  </Button>
                  <div className="min-w-0">
                    <DialogTitle className="text-lg font-bold text-gray-900">
                      Weld Management — {spool.name}
                    </DialogTitle>
                    {spoolHeaderMeta}
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap items-end justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => setIsFATSpoolModalOpen(true)}
                  >
                    <FileText className="h-4 w-4" />
                    FAT (spool)
                  </Button>
                </div>
              </DialogHeader>

              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                <div className="flex min-h-0 min-w-0 flex-wrap content-center items-center justify-start gap-x-1 gap-y-2 overflow-x-hidden py-2 px-4 sm:px-6">
                  {workflowSteps.map((step, idx) => {
                    const done = idx < currentStepIdx;
                    const active = step.stripKey === activeStripKey;
                    return (
                      <React.Fragment key={step.stripKey}>
                        <button
                          type="button"
                          onClick={() => setActiveStripKey(step.stripKey)}
                          className={`inline-flex h-8 min-h-8 max-w-full shrink-0 items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold whitespace-nowrap leading-none transition-colors ${
                            active
                              ? spoolTw.stepActivePill
                              : done
                                ? spoolTw.stepDonePill
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                        >
                          <span
                            className={`flex h-5 w-5 shrink-0 flex-none items-center justify-center rounded-full text-xs font-bold leading-none ${
                              active
                                ? spoolTw.stepActiveNum
                                : done
                                  ? spoolTw.stepDoneCheck
                                  : 'bg-gray-300 text-gray-600'
                            }`}
                          >
                            {done ? '✓' : idx + 1}
                          </span>
                          <span className="min-w-0 leading-tight inline-flex items-center gap-1">
                            {step.short}
                            {weldForSteps && workflowStepShowsRepairCue(weldForSteps, step.panel as SpoolModalWorkflowStep) ? (
                              <span className="rounded bg-amber-200/90 px-1 text-[9px] font-bold uppercase text-amber-950">
                                Repair
                              </span>
                            ) : null}
                          </span>
                        </button>
                        {idx < workflowSteps.length - 1 && (
                          <ChevronRight
                            aria-hidden
                            className={`h-3.5 w-3.5 shrink-0 self-center ${done ? spoolTw.stepDoneChevron : 'text-gray-300'}`}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>

                <div className={`mt-3 flex min-w-0 items-center justify-between px-4 py-1 sm:px-6 ${spoolTw.stepHeaderBar}`}>
                  <h3 className="text-sm font-semibold text-gray-800">
                    Step {currentStepIdx + 1} — {currentStep.label}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 ">
                    <span className={`rounded px-2.5 py-1 text-xs font-semibold ${currentStep.roleColor}`}>
                      {currentStep.role}
                    </span>
                    {showWeldTelemetryButton ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => setIsWeldTelemetryModalOpen(true)}
                      >
                        <Eye className="h-4 w-4" />
                        Weld data
                      </Button>
                    ) : null}
                  </div>
                </div>

                <div className="w-full min-w-0 px-4 py-5 sm:px-6 sm:py-6">
                  {renderPanel()}
                </div>
              </div>
            </>
            )}
        </div>
        </DialogContent>
      </Dialog>
      <NDTInspectionModal
        open={isNDTInspectionModalOpen}
        onClose={() => setIsNDTInspectionModalOpen(false)}
        project={project}
        spoolName={spool.name}
      />
      <CoatingQCModal
        open={isCoatingQCModalOpen}
        onClose={() => setIsCoatingQCModalOpen(false)}
        project={project}
        spoolName={spool.name}
      />
      <RaiseWorkflowNCRModal
        open={isRaiseNcrOpen}
        onClose={() => {
          setNcrListPrefillRow(null);
          setIsRaiseNcrOpen(false);
        }}
        project={project}
        projectId={stepPanelContext.projectId ?? null}
        pipeId={stepPanelContext.pipeId ?? null}
        spoolId={spool.id}
        spoolName={spool.name}
        weldStepType={ncrWeldStepFromStrip}
        categoryLabel={currentStep.label}
        weldId={weldForSteps?.id ?? null}
        weldRef={selectedWeld?.number ?? weldForSteps?.number ?? null}
        sig={sigProps}
        prefillListRow={ncrListPrefillRow}
      />
      <WeldTelemetryModal
        open={isWeldTelemetryModalOpen && showWeldTelemetryButton}
        onClose={() => setIsWeldTelemetryModalOpen(false)}
        spool={spool}
        weld={selectedWeld ?? weldForSteps}
      />
      <FATSpoolModal
        open={isFATSpoolModalOpen}
        onClose={() => setIsFATSpoolModalOpen(false)}
        spool={spool}
        project={project}
        projectId={project?.project?.projectId}
        pipeId={selectedpipe?.pipeId}
      />
    </>
  );
};
